import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "./FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { SingleAddressForm } from "./SingleAddressForm";
import { Estate } from "../../../models/Estate";
import { AtticusMySQL } from "../../../apis/AtticusMySQL";
import { Place } from "../../../models/Place";
import { estateApi, logger } from "../../../apis";
import { State } from "../../../models/Geography";
import { ChangeCountryDialog } from "./ChangeCountryDialog";
import { UserProfile, UserProfileJSON } from "models/UserProfile";
import { Languages } from "models/User";
import { AssetRealEstate } from "../../../models/AssetDetail";
import { AssetContext } from "../../../AssetContextWrapper";
import { PlaceAPI } from "../../../apis/PlaceAPI";

export enum Outcomes {
  address = "Address",
  noAddress = "noAddress",
  skipToEnd = "skipToEnd",
}

interface Props {
  title?: string;
  subTitle?: string;
  initWithAddressInputOnly?: boolean;
  // NOTE: SingleAddressForm has two modes: address & country/state
  //  if the above is true only init in address mode, never county/state mode
}

export const PrimaryHomeAssetScreen = ({
  title,
  subTitle,
  initWithAddressInputOnly = false,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const { screenName } = useParams();
  const {
    flow,
    activeEstate,
    setActiveEstate,
    states,
    counties,
    countyTypes,
    countries,
    user,
    setUser,
  } = useAuthContext()
  const { categories, assetsLoaded, refreshAssets } = useContext(AssetContext);
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const primaryRealEstate = activeEstate?.primaryRealEstate;

  const [initPlace, setInitPlace] = useState<Place>();
  const [initFromPlaceDbTable, setInitFromPlaceDbTable] = useState(false);
  const [placeCheckComplete, setPlaceCheckComplete] = useState(false);
  const [countryChangeDialogVisible, setCountryChangeDialogVisible] =
    useState(false);
  const [locationCountryId, setLocationCountryId] = useState<number>();
  const [userCountryId, setUserCountryId] = useState<number>();

  useEffect(() => {
    if (assetsLoaded) {
      (async () => {
        let place = undefined;
        // Check if user is authenticated and pull their current country
        if (user?.profile?.countryId) {
          setUserCountryId(parseInt(user?.profile?.countryId, 10));
        }
        // check if this estate as a primary home real estate asset set
        if (activeEstate && activeEstate?.assets) {
          if (primaryRealEstate) {
            place = new Place(
              primaryRealEstate?.id,
              undefined,
              (primaryRealEstate?.detail as AssetRealEstate)?.getFullAddress(
                states,
                countries
              ),
              (primaryRealEstate?.detail as AssetRealEstate)?.google_places_id
            );
            setInitPlace(place);
            setLocationCountryId(
              (primaryRealEstate?.detail as AssetRealEstate)?.countries_id
            );
          }
        }
        // if customer doesn't have a assets_real_estate record check places table
        if (!place) {
          // check if this estate has a primary place set
          const primaryPlace = PlaceAPI.singlePrimaryPlace(
            await PlaceAPI.getPlaces()
          );
          if (primaryPlace) {
            setInitPlace(primaryPlace);
            setInitFromPlaceDbTable(true);
          }
        }
        setPlaceCheckComplete(true);
      })();
    }
  }, [assetsLoaded]);

  const handleDialogComplete = async (
    countryId?: number,
    languageCode?: string
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");
    let outcome = Outcomes.noAddress;

    setCountryChangeDialogVisible(false);

    if (countryId != userCountryId) {
      // USER WANTS TO CHANGE THE COUNTRY TO MATCH THE PLACE.
      const editedProfileJson: UserProfileJSON = {};

      // Set New Country ID
      editedProfileJson.countries_id = countryId?.toString();

      // Set New Language Code
      if (languageCode) {
        try {
          const langId = languageCode == "en" ? Languages.en : Languages.fr;

          editedProfileJson.languages_id = langId.toString();
        } catch {
          throw "Invalid language code";
        }
      }

      // Perform API Call to Edit User Profile with new Country and Language
      await AtticusMySQL.updateUserProfile(editedProfileJson);

      // Now that the user's changed, let's get the latest version of the user into context.
      if(user && setUser) {
        user.profile = UserProfile.fromJSON(editedProfileJson)
        setUser(user)
      }

      outcome = Outcomes.address;
      flow?.navigateToNext(currentScreen, outcome, navigate);
    }
    return true;
  };

  const handleComplete = async (
    place?: Place,
    countryId?: number,
    stateId?: number
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");
    let outcome = Outcomes.noAddress;

    const estate = activeEstate || new Estate();
    if (place) {
      // User is providing a Place using Google Place API
      try {
        if (place?.placeId !== initPlace?.placeId || initFromPlaceDbTable) {
          const asset = place.toRealEstateAsset(
            activeEstate?.id,
            states,
            counties,
            countyTypes,
            countries,
            categories
          );
          if (initFromPlaceDbTable) {
            // if transitioning from places table to asset table delete the previous Place record
            if (initPlace?.id) {
              await PlaceAPI.deletePlace(initPlace.id);
            }
            // clear the id because we want to create a new asset record
            asset.id = undefined;
          }
          // save to db
          await estateApi.saveAsset(asset);

          // update the client data store
          await refreshAssets?.(true); // exclude fi

          // set the countryId and stateId on the client-side estate database
          if (place.state && place.country) {
            const theState = states?.find(
              (state: State) => state.code === place.state
            );

            // Get the countryID from the Place
            // Have to do this to immediately access value before re-rendering
            const tempLocationCountryId = place.country === "US" ? 1 : 2;
            setLocationCountryId(tempLocationCountryId);

            if (
              tempLocationCountryId &&
              userCountryId &&
              tempLocationCountryId != userCountryId
            ) {
              // If the user's country is not the same as the Place's country,
              // then we need to update the user's country
              setCountryChangeDialogVisible(true);

              throw Error("Country mismatch");
            } else {
              if (theState) {
                // set the state and country on estate record
                estate.stateId = theState?.id;
                estate.countryId = place.country === "US" ? 1 : 2;

                try {
                  await estate.saveEstate();
                  setActiveEstate(estate);
                } catch (error) {
                  throw new Error("could not save data");
                }
              } else {
                throw Error("could not determine state id from place");
              }
            }
          } else {
            throw Error("did not receive state and/or country place");
          }
        } else {
          // don't need to update the database record
        }
        outcome = Outcomes.address;
      } catch (error) {
        logger.error(error as Error);
        throw Error(`Saving address failed.`);
      }
    } else {
      // User is inputting the country and state without an address

      // Check to see if selected country matches user's country.
      if (countryId != userCountryId) {
        setLocationCountryId(countryId);
        setCountryChangeDialogVisible(true);

        throw Error("Country mismatch"); // where is this caught?
      } else {
        countryId && (estate.countryId = countryId);
        stateId && (estate.stateId = stateId);
        if (initPlace?.id) {
          // previously had place set, delete it
          await estateApi.deleteAsset(initPlace.id);
          await refreshAssets?.(true); // exclude fi);
        }
        try {
          await estate.saveEstate();
          setActiveEstate(estate);
        } catch (error) {
          throw new Error("could not save data");
        }
      }
    }

    // TODO: confirm we want to shorten on-boarding for forms app customers
    // if (user?.origin === CustomerOrigin.FormsApp) {
    //   outcome = Outcomes.skipToEnd;
    // }
    flow?.navigateToNext(currentScreen, outcome, navigate);
    return true;
  };

  const handleClear = async (): Promise<void> => {
    console.log("clear address");
  };

  let initCountryId = undefined;
  let initStateId = undefined;
  if (!initWithAddressInputOnly) {
    initCountryId =
      activeEstate?.countryId && activeEstate.countryId == userCountryId
        ? activeEstate.countryId
        : undefined;
    initStateId =
      activeEstate?.stateId && initCountryId ? activeEstate.stateId : undefined;

    if (!initCountryId) {
      initCountryId = userCountryId;
    }
  }

  let personalizedTitle =
    title?.replace(
      "#deceasedFirstName#",
      activeEstate?.deceasedFirstName || ""
    ) || "Where did the deceased live (for tax and legal purposes)?";

  if (initPlace) {
    personalizedTitle = "Just making sure... " + personalizedTitle;
  }

  return currentScreen ? (
    <FlowScreenWrapper title={personalizedTitle} subTitle={subTitle}>
      {placeCheckComplete ? (
        <>
          <SingleAddressForm
            initPlace={initPlace}
            initCountryId={initCountryId}
            initStateId={initStateId}
            onComplete={handleComplete}
            onClear={handleClear}
            onBack={() => navigate(-1)}
          />

          <ChangeCountryDialog
            open={countryChangeDialogVisible}
            handleClose={() => {
              setCountryChangeDialogVisible(false);
            }}
            onComplete={handleDialogComplete}
            newCountryId={locationCountryId}
            userCountryId={userCountryId}
          />
        </>
      ) : (
        <></>
      )}
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
